/**
 * Chat Service for AI Integration
 * 
 * Handles conversation management, content processing, and AI communication
 * for the Chat with Note feature. Uses the existing AI configuration.
 */
import { authAPI } from './api'

class ChatService {
  constructor() {
    this.conversationPrefix = 'chat_conversation_'
    // ENHANCED token limits for better chat experience
    this.maxContextLength = 12000 // Much larger for note content
    this.maxResponseTokens = 3000  // Increased for detailed responses
  }

  /**
   * Strip non-text content from note
   * Removes base64 images, attachments, recordings, and other media
   */
  stripNonTextContent(content) {
    if (!content) return ''
    
    let cleaned = content
    
    // Remove base64 images
    cleaned = cleaned.replace(/data:image\/[^;]+;base64,[^"'\s>]+/g, '[Image]')
    
    // Remove img tags
    cleaned = cleaned.replace(/<img[^>]*>/gi, '[Image]')
    
    // Remove attachment links
    cleaned = cleaned.replace(/<a[^>]*data-file-id[^>]*>.*?<\/a>/gi, '[Attachment]')
    
    // Remove audio/recording indicators
    cleaned = cleaned.replace(/🎵[^<\n]*/g, '[Audio Recording]')
    
    // Remove other media indicators
    cleaned = cleaned.replace(/📎[^<\n]*/g, '[Attachment]')
    cleaned = cleaned.replace(/🎨[^<\n]*/g, '[Drawing]')
    cleaned = cleaned.replace(/📷[^<\n]*/g, '[Image]')
    cleaned = cleaned.replace(/🎬[^<\n]*/g, '[Video]')
    
    // Strip HTML tags but preserve structure
    cleaned = this.stripHtml(cleaned)
    
    // Clean up extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim()
    
    return cleaned
  }

  /**
   * Simple HTML tag stripper
   * Removes HTML tags while preserving text content
   */
  stripHtml(html) {
    if (!html) return ''
    
    // Create a temporary div to parse HTML
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    
    // Get text content and preserve line breaks
    let text = tmp.textContent || tmp.innerText || ''
    
    // Replace multiple whitespace with single space
    text = text.replace(/\s+/g, ' ').trim()
    
    return text
  }

  /**
   * Build chat context with note content and conversation history
   */
  buildChatContext(note, conversationHistory, includeNoteContext) {
    let context = ''
    
    if (includeNoteContext && note) {
      const noteContent = this.stripNonTextContent(note.content)
      context += `Note Title: ${note.title || 'Untitled'}\n`
      context += `Note Content: ${noteContent}\n\n`
    }
    
    // Add conversation history (limit to recent messages to stay within token limits)
    if (conversationHistory.length > 0) {
      context += 'Previous conversation:\n'
      const recentMessages = conversationHistory.slice(-10) // Last 10 messages
      
      recentMessages.forEach(msg => {
        const role = msg.role === 'user' ? 'Human' : 'Assistant'
        context += `${role}: ${msg.content}\n`
      })
      context += '\n'
    }
    
    // Enhanced context management with intelligent truncation
    if (context.length > this.maxContextLength) {
      context = this.intelligentTruncate(context, this.maxContextLength)
    }
    
    return context
  }

  /**
   * Send message to AI service using existing AI configuration
   */
  async sendMessage(context, model, conversationHistory) {
    const lastMessage = conversationHistory[conversationHistory.length - 1]
    const userQuestion = lastMessage?.content || ''
    
    // Build the final prompt with required suffix
    const prompt = context + `Human: ${userQuestion} : answer this within the given context.\n\nAssistant:`
    
    try {
      // Get user's AI configuration (same as RichTextEditor)
      const aiConfig = await authAPI.getAIConfiguration()
      
      console.log('Using AI configuration for chat:', {
        url: aiConfig.aiUrl,
        model: aiConfig.aiModel,
        hasToken: !!aiConfig.aiToken
      })

      // Make API call using the same pattern as RichTextEditor
      const response = await fetch(aiConfig.aiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': aiConfig.aiToken
        },
        body: JSON.stringify({
          model: aiConfig.aiModel,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: this.maxResponseTokens, // Enhanced to 3000 tokens
          temperature: 0.7
        })
      })

      if (!response.ok) {
        throw new Error(`AI API request failed: ${response.status}`)
      }

      const data = await response.json()
      const content = data.choices?.[0]?.message?.content || 'No response received'
      
      return content.trim()
    } catch (error) {
      console.error('Chat service error:', error)
      throw new Error(`AI service failed: ${error.message}`)
    }
  }


  /**
   * Get conversation history from localStorage
   */
  getConversationHistory(noteId) {
    if (!noteId) return []
    
    try {
      const key = `${this.conversationPrefix}${noteId}`
      const saved = localStorage.getItem(key)
      return saved ? JSON.parse(saved) : []
    } catch (error) {
      console.error('Failed to load conversation history:', error)
      return []
    }
  }

  /**
   * Save conversation history to localStorage
   */
  saveConversationHistory(noteId, conversation) {
    if (!noteId) return
    
    try {
      const key = `${this.conversationPrefix}${noteId}`
      localStorage.setItem(key, JSON.stringify(conversation))
    } catch (error) {
      console.error('Failed to save conversation history:', error)
    }
  }

  /**
   * Clear conversation history
   */
  clearConversationHistory(noteId) {
    if (!noteId) return
    
    try {
      const key = `${this.conversationPrefix}${noteId}`
      localStorage.removeItem(key)
    } catch (error) {
      console.error('Failed to clear conversation history:', error)
    }
  }

  /**
   * Clear all conversation histories (for logout)
   */
  clearAllConversationHistories() {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith(this.conversationPrefix)
      )
      keys.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear all conversation histories:', error)
    }
  }

  /**
   * Estimate token count (rough approximation: 1 token ≈ 4 characters)
   */
  estimateTokens(text) {
    return Math.ceil(text.length / 4)
  }

  /**
   * Intelligent truncation that preserves sentence boundaries
   */
  intelligentTruncate(text, maxLength) {
    if (text.length <= maxLength) return text
    
    // Try to cut at sentence boundary
    const truncated = text.substring(0, maxLength)
    const lastSentence = truncated.lastIndexOf('.')
    const lastNewline = truncated.lastIndexOf('\n')
    
    const cutPoint = Math.max(lastSentence, lastNewline)
    return cutPoint > maxLength * 0.8 ? truncated.substring(0, cutPoint) : truncated
  }
}

// Export singleton instance
export const chatService = new ChatService()
export default chatService
